import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Bell, Users, Check, Trophy, X, Loader2, 
  ChevronRight, Clock, CheckCheck
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NOTIF_ICONS = {
  league_join_request: Users,
  league_request_accepted: Check,
  league_request_rejected: X,
  league_member_kicked: X,
  transfer_window_opened: Trophy,
  match_reminder: Bell,
  admin_broadcast: Bell,
  daily_reward: Trophy,
  manager_of_day: Trophy,
};

const NOTIF_COLORS = {
  league_join_request: "text-blue-400",
  league_request_accepted: "text-emerald-400",
  league_request_rejected: "text-red-400",
  league_member_kicked: "text-red-400",
  transfer_window_opened: "text-amber-400",
  match_reminder: "text-emerald-400",
  admin_broadcast: "text-purple-400",
  daily_reward: "text-amber-400",
  manager_of_day: "text-amber-400",
};

const sportChip = (notif) => {
  const sport = notif.sport || notif.data?.sport;
  if (sport === "football") return "⚽";
  if (sport === "cricket") return "🏏";
  return null;
};

function formatTimeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function NotificationDropdown({ onClose, onUnreadUpdate }) {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${API}/notifications`, { headers: getAuthHeader() });
        setNotifications(res.data.notifications || []);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [getAuthHeader]);

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeader() });
        setNotifications(prev => 
          prev.map(n => n.id === notif.id ? { ...n, read: true } : n)
        );
        onUnreadUpdate(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error("Failed to mark notification as read:", err);
      }
    }
    
    // Navigate
    if (notif.data?.deep_link) {
      navigate(notif.data.deep_link);
      onClose();
    } else if (notif.data?.league_id) {
      navigate(`/leagues`);
      onClose();
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      onUnreadUpdate(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="fixed right-2 left-2 top-16 sm:absolute sm:right-0 sm:left-auto sm:top-12 sm:w-80 bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden z-[100]" data-testid="notification-dropdown" style={{ maxHeight: "70vh" }}>
      {/* Header */}
      <div className="p-3 border-b border-slate-700 flex items-center justify-between bg-slate-900/50">
        <h3 className="text-white font-semibold text-sm">Notifications</h3>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markingAll}
            className="text-emerald-400 text-xs hover:underline flex items-center gap-1"
          >
            {markingAll ? <Loader2 className="animate-spin" size={12} /> : <CheckCheck size={12} />}
            Mark all read
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-slate-500" size={24} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-8 text-center">
            <Bell className="mx-auto text-slate-600 mb-2" size={24} />
            <p className="text-slate-500 text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map((notif) => {
            const Icon = NOTIF_ICONS[notif.type] || Bell;
            const colorClass = NOTIF_COLORS[notif.type] || "text-slate-400";
            
            return (
              <button
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`w-full p-3 flex items-start gap-3 hover:bg-slate-700/50 transition-colors border-b border-slate-700/50 last:border-0 ${
                  !notif.read ? "bg-slate-700/30" : ""
                }`}
                data-testid={`notification-${notif.id}`}
              >
                <div className={`w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 text-left">
                  <p className={`text-sm ${!notif.read ? "text-white font-medium" : "text-slate-300"}`}>
                    {sportChip(notif) && <span className="mr-1">{sportChip(notif)}</span>}
                    {notif.title}
                  </p>
                  <p className="text-slate-500 text-xs line-clamp-2">{notif.body}</p>
                  <p className="text-slate-600 text-xs mt-1 flex items-center gap-1">
                    <Clock size={10} />
                    {formatTimeAgo(notif.created_at)}
                  </p>
                </div>
                {!notif.read && (
                  <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0 mt-2" />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
